import { validateReportingPeriodV01, type ReportingPeriodV01 } from './advisoryReportingPeriod';
import { isEngagementEngine, type EngagementEngine } from './advisoryEngine';

export type ReviewField = 'subjectCompany' | 'counterparties' | 'period';
export type ReviewConflictField = ReviewField | 'counterpartyKind';
export type ReviewSegment = { type: 'text'; text: string } | { type: 'field'; field: ReviewField };
export interface ReviewValues {
  subjectCompany: string;
  counterparties: string[];
  period: ReportingPeriodV01;
}
export interface ReviewChanges extends Partial<ReviewValues> {
  feedbackDraft?: string;
}
export interface ReviewResolution {
  field: ReviewConflictField;
  decision: 'keepManual' | 'acceptSuggestion';
}
export type ReviewRequest =
  | {
      operation: 'prepare';
      requestId: string;
      userQuery: string;
      workspaceVersion?: 1;
      engine?: EngagementEngine;
    }
  | {
      operation: 'update';
      requestId: string;
      expectedRevision: number;
      changes: ReviewChanges;
      resolutions?: ReviewResolution[];
    }
  | { operation: 'refine' | 'confirm'; requestId: string; expectedRevision: number };
export interface ReviewSnapshot {
  engine?: EngagementEngine;
  schemaVersion: 1;
  projectId: string;
  revision: number;
  status: 'preparing' | 'ready' | 'needsAttention' | 'approved';
  originalQuery: string;
  statement: { schemaVersion: 1; segments: ReviewSegment[] };
  renderedGoal: string;
  values: ReviewValues;
  suggestions: ReviewValues;
  manualOverrides: ReviewField[];
  periodDefaulted: boolean;
  periodResolution: 'proposed' | 'corrected' | 'currentYear';
  engagement: null | {
    engagementId: string;
    name: string;
    description: string;
    confidence: string;
    rationale: string;
  };
  counterpartyKind: '' | 'competitor' | 'acquisition target';
  conflicts: Array<{
    field: ReviewConflictField;
    manualValue: string | string[] | ReportingPeriodV01;
    suggestedValue: string | string[] | ReportingPeriodV01;
  }>;
  feedbackDraft: string;
  errors: Array<{ field: string; code: string }>;
  canConfirm: boolean;
  canRefine: boolean;
  refinementsRemaining: number;
  approvedAt: string | null;
  downstream: { status: 'notStarted' | 'pending' | 'running' | 'completed' | 'failed' };
}
/** Empty clarification cannot be classified, but independent failures remain retryable. */
export function hasReviewPreparationFailure(snapshot: ReviewSnapshot | null): boolean {
  if (!snapshot) return false;
  const emptyClarification =
    !snapshot.renderedGoal &&
    snapshot.errors.some(error => error.field === 'goal' && error.code === 'clarificationRequired');
  return snapshot.errors.some(
    error =>
      error.code === 'extractionFailed' ||
      (error.code === 'classificationFailed' && !emptyClarification)
  );
}

export type ReviewProgressStage = 'preparing' | 'understandingDetails' | 'shapingEngagement';
export interface ReviewProgress {
  schemaVersion: 1;
  projectId: string;
  requestId: string;
  revision: number;
  stage: ReviewProgressStage;
}

/** Transient progress only describes an initial prepare request, never approval. */
export function parseReviewProgress(
  raw: unknown,
  request: ReviewRequest,
  projectId: string
): ReviewProgress | null {
  if (
    !object(raw) ||
    request.operation !== 'prepare' ||
    raw.schemaVersion !== 1 ||
    raw.projectId !== projectId ||
    raw.requestId !== request.requestId ||
    raw.revision !== 1 ||
    !oneOf(raw.stage, ['preparing', 'understandingDetails', 'shapingEngagement'] as const)
  )
    return null;
  return {
    schemaVersion: 1,
    projectId,
    requestId: request.requestId,
    revision: 1,
    stage: raw.stage,
  };
}

export interface ReviewConfirmed {
  projectId: string;
  revision: number;
  requestId: string;
  confirmedGoal: string;
}

const object = (v: unknown): v is Record<string, unknown> =>
  Boolean(v) && typeof v === 'object' && !Array.isArray(v);
const oneOf = <T extends string>(v: unknown, options: readonly T[]): v is T =>
  typeof v === 'string' && options.includes(v as T);
const integer = (v: unknown, min = 0): v is number =>
  typeof v === 'number' && Number.isSafeInteger(v) && v >= min;
const strings = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every(s => typeof s === 'string');
const fields: ReviewField[] = ['subjectCompany', 'counterparties', 'period'];
const conflictFields: ReviewConflictField[] = [...fields, 'counterpartyKind'];
function parseValues(v: unknown): ReviewValues | null {
  if (!object(v) || typeof v.subjectCompany !== 'string' || !strings(v.counterparties)) return null;
  const period = validateReportingPeriodV01(v.period);
  return period.ok
    ? {
        subjectCompany: v.subjectCompany,
        counterparties: [...v.counterparties],
        period: period.value,
      }
    : null;
}
function conflictValue(v: unknown): string | string[] | ReportingPeriodV01 | null {
  if (typeof v === 'string') return v;
  if (strings(v)) return [...v];
  const period = validateReportingPeriodV01(v);
  return period.ok ? period.value : null;
}

/** Build a renderable allowlist; raw provider errors and internal receipts never enter UI state. */
export function parseReviewSnapshot(raw: unknown, projectId: string): ReviewSnapshot | null {
  if (
    !object(raw) ||
    raw.schemaVersion !== 1 ||
    raw.projectId !== projectId ||
    (raw.engine !== undefined && !isEngagementEngine(raw.engine)) ||
    !integer(raw.revision, 1) ||
    !oneOf(raw.status, ['preparing', 'ready', 'needsAttention', 'approved'] as const) ||
    typeof raw.originalQuery !== 'string' ||
    typeof raw.renderedGoal !== 'string' ||
    !object(raw.statement) ||
    raw.statement.schemaVersion !== 1 ||
    !Array.isArray(raw.statement.segments)
  )
    return null;
  const segments: ReviewSegment[] = [];
  for (const s of raw.statement.segments) {
    if (!object(s)) return null;
    if (s.type === 'text' && typeof s.text === 'string' && Object.keys(s).length === 2)
      segments.push({ type: 'text', text: s.text });
    else if (s.type === 'field' && oneOf(s.field, fields) && Object.keys(s).length === 2)
      segments.push({ type: 'field', field: s.field });
    else return null;
  }
  const values = parseValues(raw.values);
  const suggestions = parseValues(raw.suggestions);
  if (
    !values ||
    !suggestions ||
    !Array.isArray(raw.manualOverrides) ||
    !raw.manualOverrides.every(f => oneOf(f, fields)) ||
    typeof raw.periodDefaulted !== 'boolean' ||
    !oneOf(raw.periodResolution, ['proposed', 'corrected', 'currentYear'] as const) ||
    !oneOf(raw.counterpartyKind, ['', 'competitor', 'acquisition target'] as const) ||
    !Array.isArray(raw.conflicts) ||
    typeof raw.feedbackDraft !== 'string' ||
    !Array.isArray(raw.errors) ||
    typeof raw.canConfirm !== 'boolean' ||
    typeof raw.canRefine !== 'boolean' ||
    !integer(raw.refinementsRemaining) ||
    !(
      raw.approvedAt === null ||
      (typeof raw.approvedAt === 'string' && !Number.isNaN(Date.parse(raw.approvedAt)))
    ) ||
    !object(raw.downstream) ||
    !oneOf(raw.downstream.status, [
      'notStarted',
      'pending',
      'running',
      'completed',
      'failed',
    ] as const)
  )
    return null;
  let engagement: ReviewSnapshot['engagement'] = null;
  if (raw.engagement !== null) {
    if (!object(raw.engagement)) return null;
    const e = raw.engagement;
    if (
      ![e.engagementId, e.name, e.description, e.confidence, e.rationale].every(
        v => typeof v === 'string'
      )
    )
      return null;
    engagement = {
      engagementId: e.engagementId as string,
      name: e.name as string,
      description: e.description as string,
      confidence: e.confidence as string,
      rationale: e.rationale as string,
    };
  }
  const conflicts: ReviewSnapshot['conflicts'] = [];
  for (const c of raw.conflicts) {
    if (!object(c) || !oneOf(c.field, conflictFields)) return null;
    const manualValue = conflictValue(c.manualValue),
      suggestedValue = conflictValue(c.suggestedValue);
    if (manualValue === null || suggestedValue === null) return null;
    conflicts.push({ field: c.field, manualValue, suggestedValue });
  }
  const errors: ReviewSnapshot['errors'] = [];
  for (const e of raw.errors) {
    if (
      !object(e) ||
      !oneOf(e.field, [
        'goal',
        'subjectCompany',
        'period',
        'counterparties',
        'engagement',
      ] as const) ||
      typeof e.code !== 'string'
    )
      return null;
    errors.push({ field: e.field, code: e.code });
  }
  if (raw.status === 'approved' && (!raw.approvedAt || !engagement)) return null;
  return {
    schemaVersion: 1,
    projectId,
    revision: raw.revision,
    ...(raw.engine === undefined ? {} : { engine: raw.engine }),
    status: raw.status,
    originalQuery: raw.originalQuery,
    statement: { schemaVersion: 1, segments },
    renderedGoal: raw.renderedGoal,
    values,
    suggestions,
    manualOverrides: [...raw.manualOverrides] as ReviewField[],
    periodDefaulted: raw.periodDefaulted,
    periodResolution: raw.periodResolution,
    engagement,
    counterpartyKind: raw.counterpartyKind,
    conflicts,
    feedbackDraft: raw.feedbackDraft,
    errors,
    canConfirm: raw.canConfirm,
    canRefine: raw.canRefine,
    refinementsRemaining: raw.refinementsRemaining,
    approvedAt: raw.approvedAt,
    downstream: { status: raw.downstream.status },
  };
}

export function parseReviewConfirmed(raw: unknown, projectId: string): ReviewConfirmed | null {
  if (
    !object(raw) ||
    raw.projectId !== projectId ||
    !integer(raw.revision, 1) ||
    typeof raw.requestId !== 'string' ||
    !raw.requestId.trim() ||
    typeof raw.confirmedGoal !== 'string' ||
    !raw.confirmedGoal.trim()
  )
    return null;
  return {
    projectId,
    revision: raw.revision,
    requestId: raw.requestId,
    confirmedGoal: raw.confirmedGoal,
  };
}

/** Same-revision recovery needs a current read or response; late stream frames stay ignored. */
export function newerReview(
  previous: ReviewSnapshot | null,
  incoming: ReviewSnapshot,
  options: { allowPreparationRecovery?: boolean; ignoreStalePreparationFailure?: boolean } = {}
): ReviewSnapshot {
  if (
    !previous ||
    previous.projectId !== incoming.projectId ||
    incoming.revision > previous.revision
  )
    return incoming;
  if (incoming.revision < previous.revision) return previous;
  if (previous.status === 'approved' && incoming.status !== 'approved') return previous;
  // Recovery may keep its revision, so the caller must establish whether this
  // failure predates it. Current responses can legitimately revalidate readiness.
  if (incoming.status === 'needsAttention' && options.ignoreStalePreparationFailure)
    return previous;
  if (
    previous.status !== 'preparing' &&
    incoming.status === 'preparing' &&
    !(previous.status === 'needsAttention' && options.allowPreparationRecovery)
  )
    return previous;
  if (
    previous.status === 'approved' &&
    previous.downstream.status === 'completed' &&
    incoming.downstream.status !== 'completed'
  )
    return previous;
  return incoming;
}

export function isUnifiedReviewEnrollmentEnabled(
  env: Record<string, string | undefined> = process.env
): boolean {
  return env.REACT_APP_ADVISORY_UNIFIED_REVIEW_ENABLED === 'true';
}
